import { getThumbnailUrl } from "../../utils/urlConfig";
import React, { useState } from "react";
import styles from "./AssetsTree.module.css";
import { FaChevronDown, FaChevronRight, FaBoxOpen, FaEye, FaTag } from "react-icons/fa";

/**
 * AssetsTree Component
 * Renders a hierarchical tree view of assets based on parent-child relationships.
 */
const AssetsTree = ({ assets, onViewDetail }) => {
    // Build tree structure from flat assets list
    const buildTree = (data) => {
        const map = {};
        data.forEach((asset) => {
            map[asset.id] = { ...asset, children: [] };
        });

        const root = [];
        data.forEach((asset) => {
            if (asset.parentAssetId && map[asset.parentAssetId]) {
                map[asset.parentAssetId].children.push(map[asset.id]);
            } else {
                root.push(map[asset.id]);
            }
        });

        return root;
    };

    const treeData = buildTree(assets);

    return (
        <div className={styles.treeContainer}>
            {treeData.length > 0 ? (
                treeData.map((node) => (
                    <TreeNode key={node.id} node={node} onViewDetail={onViewDetail} depth={0} />
                ))
            ) : (
                <p className={styles.noData}>No hierarchy found for the current items.</p>
            )}
        </div>
    );
};

/**
 * TreeNode Component
 * Recursive component to render each branch of the asset tree.
 */
const TreeNode = ({ node, onViewDetail, depth }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const hasChildren = node.children && node.children.length > 0;

    const toggleExpand = (e) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    };

    return (
        <div className={styles.nodeWrapper}>
            <div
                className={styles.nodeRow}
                style={{ paddingLeft: `${depth * 24}px` }}
                onClick={toggleExpand}
            >
                <div className={styles.iconContainer}>
                    {hasChildren ? (
                        isExpanded ? <FaChevronDown className={styles.toggleIcon} /> : <FaChevronRight className={styles.toggleIcon} />
                    ) : (
                        <div className={styles.emptySpacer} />
                    )}
                </div>

                <div className={styles.thumbnailContainer}>
                    {node.images && node.images.length > 0 ? (
                        <img
                            src={getThumbnailUrl(node.images[0])}
                            className={styles.thumbnail}
                            alt={node.name}
                        />
                    ) : (
                        <div className={styles.thumbnailPlaceholder}>
                            <FaBoxOpen />
                        </div>
                    )}
                </div>

                <div className={styles.nodeContent}>
                    <span className={styles.nodeName}>
                        {node.name}
                        {node.tags && node.tags.length > 0 && (
                            <div style={{ display: 'inline-flex', gap: '4px', marginLeft: '8px', verticalAlign: 'middle' }}>
                                {node.tags.map(tag => (
                                    <span 
                                        key={tag.id}
                                        style={{ 
                                            backgroundColor: tag.color ? `${tag.color}33` : '#e2e8f0',
                                            border: `1px solid ${tag.color || '#cbd5e1'}`,
                                            color: 'var(--text-color)',
                                            padding: '1px 4px',
                                            borderRadius: '4px',
                                            fontSize: '10px',
                                            display: 'inline-flex',
                                            alignItems: 'center'
                                        }}
                                        title={tag.name}
                                    >
                                        <FaTag style={{ fontSize: '8px', marginRight: '3px', color: tag.color || 'inherit' }} />
                                        {tag.name}
                                    </span>
                                ))}
                            </div>
                        )}
                    </span>
                    <span className={styles.nodeId}>#{node.id}</span>
                    {node.manufacturer && <span className={styles.nodeMeta}>{node.manufacturer}</span>}
                </div>

                <div className={styles.actions}>
                    <button
                        className={styles.viewButton}
                        onClick={(e) => { e.stopPropagation(); onViewDetail(node.id); }}
                        title="View Details"
                    >
                        <FaEye />
                    </button>
                </div>
            </div>

            {hasChildren && isExpanded && (
                <div className={styles.childrenWrapper}>
                    {node.children.map((child) => (
                        <TreeNode key={child.id} node={child} onViewDetail={onViewDetail} depth={depth + 1} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default AssetsTree;
